import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import axios from 'axios';
import { trainingService } from '../services/trainingService';
import { formatDateDisplay } from '../utils/dateUtils';

const TrainingSignatureDetailDialog = ({ open, onClose, completion, which }) => {
  const [imageUrl, setImageUrl] = useState('');
  const [loadingImage, setLoadingImage] = useState(false);

  const isEmployee = which === 'employee';
  const title = isEmployee ? 'Employee signature' : 'Supervisor signature';

  const name = isEmployee
    ? (completion?.employee_signature_text
      || [completion?.first_name, completion?.last_name].filter(Boolean).join(' '))
    : (completion?.supervisor_signature_text
      || [completion?.supervisor_first_name, completion?.supervisor_last_name].filter(Boolean).join(' '));

  const email = isEmployee ? completion?.employee_email : completion?.supervisor_email;
  const signedAt = isEmployee ? completion?.employee_signed_at : completion?.supervisor_signed_at;
  const hasImage = isEmployee
    ? completion?.employee_signature_image
    : completion?.supervisor_signature_image;

  useEffect(() => {
    if (!open || !completion?.id || !hasImage) {
      setImageUrl('');
      return undefined;
    }

    let objectUrl;
    let cancelled = false;
    setLoadingImage(true);
    (async () => {
      try {
        const res = await axios.get(
          trainingService.getSignatureImageUrl(completion.id, which),
          { responseType: 'blob' }
        );
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setImageUrl(objectUrl);
      } catch {
        if (!cancelled) setImageUrl('');
      } finally {
        if (!cancelled) setLoadingImage(false);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, completion?.id, which, hasImage]);

  const handleClose = () => {
    setImageUrl('');
    onClose();
  };

  if (!completion) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 0.75 }}>
          <strong>Name:</strong> {name || '—'}
        </Typography>
        <Typography variant="body2" sx={{ mb: 0.75 }}>
          <strong>Login:</strong> {email || '—'}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>Signed:</strong> {signedAt ? formatDateDisplay(signedAt) : '—'}
        </Typography>

        {hasImage ? (
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'grey.50',
              minHeight: 120,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
            }}
          >
            {loadingImage ? (
              <CircularProgress size={28} />
            ) : imageUrl ? (
              <Box
                component="img"
                src={imageUrl}
                alt={title}
                sx={{ maxWidth: '100%', maxHeight: 160, objectFit: 'contain' }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">Signature image unavailable</Typography>
            )}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No signature image on file (signed before drawn signatures were enabled).
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TrainingSignatureDetailDialog;
